import {
  FileId,
  TprmExternalQuestionnaireId,
  TprmQuestionTemplateId,
  File as FileT,
  AccountMin,
  ThirdPartyId,
  EngagementId,
  InvitePortalUser,
  CreateFileUploadSession,
  FileUploadSessionId,
  PortalReportSnapshotId,
  ConsolidatedSuggestionId,
  SuggestionDocumentId,
  TprmTableRowId,
  TprmExternalColumnValue,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {uploadImpl} from "../files";
import {platformedClient} from "..";

export async function getLegacyQuestionnaireThirdPartyId(
  account: AccountMin,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/legacy_questionnaire/{external_questionnaire_id}/third_party_id",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        external_questionnaire_id: externalQuestionnaireId,
      },
    },
  );
}

export async function getExternalQuestionnaire(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
    },
  );
}

export async function getExternalQuestionnaireTemplate(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/template",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
    },
  );
}

export async function applyConsolidatedSuggestion(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  consolidatedSuggestionId: ConsolidatedSuggestionId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/apply_consolidated_suggestion",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: {consolidated_suggestion_id: consolidatedSuggestionId},
    },
  );
  await invalidateQueries([
    q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId),
    q.external.portal.suggestions.filter(account, thirdPartyId, externalQuestionnaireId),
  ]);
  return res;
}

export async function undoSuggestion(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/clear_suggestion",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
    },
  );
  await invalidateQueries([
    q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId),
    q.external.portal.suggestions.filter(account, thirdPartyId, externalQuestionnaireId),
  ]);
  return res;
}

export async function requestAiFeedback(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/request_ai_feedback",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
    },
  );
  await invalidateQueries([
    q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId),
    q.external.portal.suggestions.filter(account, thirdPartyId, externalQuestionnaireId),
  ]);
  return res;
}

export async function dismissConsolidatedSuggestion(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  consolidatedSuggestionId: ConsolidatedSuggestionId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/consolidated_suggestions/dismiss",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
      body: {consolidated_suggestion_id: consolidatedSuggestionId},
    },
  );
  await invalidateQueries([q.external.portal.suggestions.filter(account, thirdPartyId, externalQuestionnaireId)]);
  return res;
}

export async function restoreConsolidatedSuggestions(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  consolidatedSuggestionIds: ConsolidatedSuggestionId[],
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/consolidated_suggestions/restore",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
      body: {consolidated_suggestion_ids: consolidatedSuggestionIds},
    },
  );
  await invalidateQueries([q.external.portal.suggestions.filter(account, thirdPartyId, externalQuestionnaireId)]);
  return res;
}

export async function listSuggestions(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/suggestions",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
    },
  );
}

export async function listSuggestionDocuments(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/suggestion_documents",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
    },
  );
}

export async function listSuggestionGenerationTasks(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/running_tasks",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
    },
  );
}

export async function uploadSuggestionDocument(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  file: File,
  progressFn?: (progress: number) => void,
  signal?: AbortSignal,
): Promise<FileT> {
  const url = platformedClient.externalUrl(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/answering/upload_suggestion_document",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
    },
  );
  const res = await uploadImpl(url, file, progressFn, signal);
  await invalidateQueries([
    q.external.portal.suggestionDocuments.filter(account, thirdPartyId, externalQuestionnaireId),
    q.external.portal.suggestionGenerationTasks.filter(account, thirdPartyId, externalQuestionnaireId),
    q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId),
  ]);
  return res;
}

export async function deleteSuggestionDocument(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  suggestionDocumentId: SuggestionDocumentId,
) {
  const res = await platformedClient.externalRequest(
    "delete",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/suggestion_documents/{suggestion_document_id}",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        suggestion_document_id: suggestionDocumentId,
      },
    },
  );
  await invalidateQueries([
    q.external.portal.suggestionDocuments.filter(account, thirdPartyId, externalQuestionnaireId),
    q.external.portal.suggestions.filter(account, thirdPartyId, externalQuestionnaireId),
    q.external.portal.suggestionGenerationTasks.filter(account, thirdPartyId, externalQuestionnaireId),
    q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId),
  ]);
  return res;
}

export async function retrySuggestionDocumentExtraction(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  suggestionDocumentId: SuggestionDocumentId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/suggestion_documents/{suggestion_document_id}/retry_extraction",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        suggestion_document_id: suggestionDocumentId,
      },
    },
  );
  await invalidateQueries([
    q.external.portal.suggestionDocuments.filter(account, thirdPartyId, externalQuestionnaireId),
    q.external.portal.suggestionGenerationTasks.filter(account, thirdPartyId, externalQuestionnaireId),
  ]);
  return res;
}

export async function triggerSuggestFromDocuments(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/answering/suggest_from_documents",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
    },
  );
  await invalidateQueries([
    q.external.portal.suggestionGenerationTasks.filter(account, thirdPartyId, externalQuestionnaireId),
    q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId),
  ]);
  return res;
}

export async function updateTextAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  text: string,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/update_text",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: text,
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);
  return res;
}

export async function updateSelectAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  values: string[],
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/update_select",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: values,
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function updateNotApplicable(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  notApplicable: boolean,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/not_applicable",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: notApplicable,
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function addFilesToAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  fileIds: FileId[],
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/add_files",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: fileIds,
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function removeFileFromAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  fileId: FileId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/remove_file",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: fileId,
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function addSubQuestionnaireToAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/add_sub_questionnaire",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function removeSubQuestionnaireFromAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  subExternalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/remove_sub_questionnaire",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: subExternalQuestionnaireId,
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function addRow(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  values: Record<string, TprmExternalColumnValue> = {},
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/add_row",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: {values},
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function removeRow(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  rowId: TprmTableRowId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/remove_row",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: {row_id: rowId},
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function setColumn(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  rowIds: TprmTableRowId[],
  columnKey: string,
  value: TprmExternalColumnValue | null,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/set_column",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: {row_ids: rowIds, column_key: columnKey, value},
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function moveRow(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  rowId: TprmTableRowId,
  targetIndex: number,
  values: Record<string, TprmExternalColumnValue> = {},
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/move_row",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: {row_id: rowId, target_index: targetIndex, values},
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function moveRows(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  rowIds: TprmTableRowId[],
  targetIndex: number,
  values: Record<string, TprmExternalColumnValue> = {},
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/question/{question_template_id}/move_rows",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
        question_template_id: questionTemplateId,
      },
      body: {row_ids: rowIds, target_index: targetIndex, values},
    },
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export function uploadFile(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  file: File,
  progressFn?: (progress: number) => void,
  signal?: AbortSignal,
): Promise<FileT> {
  return uploadImpl(
    platformedClient.externalUrl(
      "post",
      "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/files",
      {
        path: {
          region: account.region,
          account_id: account.account_id,
          third_party_id: thirdPartyId,
          external_questionnaire_id: externalQuestionnaireId,
        },
      },
    ),
    file,
    progressFn,
    signal,
  );
}

export function downloadFile(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  fileId: FileId,
) {
  window.open(
    platformedClient.externalUrl(
      "get",
      "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/files/{file_id}",
      {
        path: {
          region: account.region,
          account_id: account.account_id,
          third_party_id: thirdPartyId,
          external_questionnaire_id: externalQuestionnaireId,
          file_id: fileId,
        },
      },
    ),
    "_blank",
  );
}

export function downloadSuggestionDocument(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  suggestionDocumentId: SuggestionDocumentId,
) {
  window.open(
    platformedClient.externalUrl(
      "get",
      "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/suggestion_documents/{suggestion_document_id}/download",
      {
        path: {
          region: account.region,
          account_id: account.account_id,
          third_party_id: thirdPartyId,
          external_questionnaire_id: externalQuestionnaireId,
          suggestion_document_id: suggestionDocumentId,
        },
      },
    ),
    "_blank",
  );
}

export async function submitExternalQuestionnaire(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/submit",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
    },
  );
  await invalidateQueries([
    q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, externalQuestionnaireId),
    q.external.portal.externalQuestionnaires.filter(account),
  ]);
  return res;
}

export async function acceptInviteKey(account: AccountMin, thirdPartyId: ThirdPartyId, inviteKey: string) {
  return await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/accept_invite_key",
    {path: {region: account.region, account_id: account.account_id, third_party_id: thirdPartyId}, body: inviteKey},
  );
}

export async function validateInvite(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  email: string,
  token: string | null,
) {
  return await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/validate_invite",
    {
      path: {region: account.region, account_id: account.account_id, third_party_id: thirdPartyId},
      body: {email, token},
    },
  );
}

export async function resendInvite(account: AccountMin, thirdPartyId: ThirdPartyId, email: string) {
  return await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/resend_invite",
    {path: {region: account.region, account_id: account.account_id, third_party_id: thirdPartyId}, body: {email}},
  );
}

export async function listEngagements(account: AccountMin, thirdPartyId: ThirdPartyId) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/engagements",
    {path: {region: account.region, account_id: account.account_id, third_party_id: thirdPartyId}},
  );
}

export async function listExternalQuestionnaires(account: AccountMin, engagementId: EngagementId) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{engagement_id}/external_questionnaires",
    {path: {region: account.region, account_id: account.account_id, engagement_id: engagementId}},
  );
}

export async function listPortalUsers(account: AccountMin, thirdPartyId: ThirdPartyId) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/portal_users",
    {path: {region: account.region, account_id: account.account_id, third_party_id: thirdPartyId}},
  );
}

export async function getPortal(account: AccountMin, thirdPartyId: ThirdPartyId) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}",
    {path: {region: account.region, account_id: account.account_id, third_party_id: thirdPartyId}},
  );
}

export async function invitePortalUser(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  payload: InvitePortalUser[],
): Promise<string> {
  const res = await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/invite_user",
    {path: {region: account.region, account_id: account.account_id, third_party_id: thirdPartyId}, body: payload},
  );
  await invalidateQueries([q.external.portal.portalUsers.filter()]);
  return res;
}

export async function listPortalReports(account: AccountMin, thirdPartyId: ThirdPartyId) {
  return await platformedClient.externalRequest(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/reports",
    {path: {region: account.region, account_id: account.account_id, third_party_id: thirdPartyId}},
  );
}

export function getPortalReportUrl(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  snapshotId: PortalReportSnapshotId,
) {
  return platformedClient.externalUrl(
    "get",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/reports/{portal_report_snapshot_id}",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        portal_report_snapshot_id: snapshotId,
      },
    },
  );
}

export async function createUploadSession(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  create: CreateFileUploadSession,
) {
  return await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/files/upload_session/create",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
      body: create,
    },
  );
}

export async function completeUploadSession(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  id: FileUploadSessionId,
): Promise<FileT> {
  return await platformedClient.externalRequest(
    "post",
    "/region/{region}/external_api/accounts/{account_id}/portal/{third_party_id}/questionnaire/{external_questionnaire_id}/files/upload_session/complete",
    {
      path: {
        region: account.region,
        account_id: account.account_id,
        third_party_id: thirdPartyId,
        external_questionnaire_id: externalQuestionnaireId,
      },
      body: id,
    },
  );
}
